import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { Quote } from '@/@types/responses/quote/Quote'
import { QuoteDetails } from '@/@types/responses/quote/QuoteDetails'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { CreateQuoteMessage } from '@/@types/messages/CreateQuoteMessage'
import { CreateQuotePartMessage } from '@/@types/messages/CreateQuotePartMessage'
import { UpdateQuoteMessage } from '@/@types/messages/UpdateQuoteMessage'
import { CreateQuoteServiceMessage } from '@/@types/messages/CreateQuoteServiceMessage'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'
import { ObjectsCreatedResponse } from '@/@types/responses/common/ObjectsCreatedResponse'

export class QuoteService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createOrder(quoteId: number): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${quoteId}/orders`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createQuote(createQuoteMessage: CreateQuoteMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createQuoteMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createQuoteFromQuote(quoteId: number): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${quoteId}`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createQuoteNote(quoteId: number, noteMessage: NoteMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(noteMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${quoteId}/notes`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createQuotePart(quoteId: number, createQuotePartMessage: CreateQuotePartMessage): Promise<ObjectsCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createQuotePartMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${quoteId}/quote-parts`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectsCreatedResponse<number>(json.ids)
    })
  }

  public createQuoteService(quoteId: number, createQuoteServiceMessage: CreateQuoteServiceMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createQuoteServiceMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${quoteId}/quote-services`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteQuote(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getQuote(id: number): Promise<QuoteDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new QuoteDetails(json.id, json.sentForCustomerApproval, json.approvedDate, json.approvedByUser, json.customerSuppliedParts, json.total, json.cartridgeId, json.customerId, json.customer, json.buildTypeId, json.notes, json.quoteParts, json.quoteServices, json.partsDiscountPercentage, json.partsTotal, json.servicesDiscountPercentage, json.servicesTotal, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public searchQuotes(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, buildTypeId: number|undefined, cartridgeId: number|undefined, customerId: number|undefined, isApproved: boolean, ascending: boolean): Promise<PagingResponse<Quote>> {
    let queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    if (buildTypeId)
      queryString += `&buildTypeId=${buildTypeId}`

    if (cartridgeId)
      queryString += `&cartridgeId=${cartridgeId}`

    if (customerId)
      queryString += `&customerId=${customerId}`

    if (isApproved)
      queryString += '&isApproved=true'

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<Quote>
    })
  }

  public updateQuote(quoteId: number, updateQuoteMessage: UpdateQuoteMessage): Promise<void> {
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateQuoteMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/quotes/${quoteId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
